// Admin Analytics & Tracking Settings — GA4, GTM, Meta Pixel, custom scripts
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Loader2, BarChart2, AlertTriangle } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const GA4_REGEX = /^G-[A-Z0-9]+$/i;
const GTM_REGEX = /^GTM-[A-Z0-9]+$/i;
const META_PIXEL_REGEX = /^\d{10,16}$/;
const INLINE_SCRIPT_REGEX = /<script(?![^>]*\bsrc=)[^>]*>/i;

const DEFAULTS = {
  ga4_measurement_id: '',
  ga4_enabled: false,
  gtm_container_id: '',
  gtm_enabled: false,
  meta_pixel_id: '',
  meta_pixel_enabled: false,
  custom_head_scripts: '',
  custom_body_scripts: '',
  analytics_enabled: true,
};

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg).join(', ');
  return fallback;
};

export const AdminAnalyticsSettings = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/settings/analytics`, { headers });
        setForm({ ...DEFAULTS, ...res.data });
        setUpdatedAt(res.data?.updated_at || null);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load analytics settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const validateOnBlur = (field, regex, message) => {
    const value = form[field];
    setErrors((e) => ({ ...e, [field]: value && !regex.test(value) ? message : null }));
  };

  const onGa4Blur = () => validateOnBlur('ga4_measurement_id', GA4_REGEX, 'Must start with "G-" followed by letters/numbers, e.g. G-XXXXXXXXXX');
  const onGtmBlur = () => validateOnBlur('gtm_container_id', GTM_REGEX, 'Must start with "GTM-", e.g. GTM-XXXXXXX');
  const onMetaPixelBlur = () => validateOnBlur('meta_pixel_id', META_PIXEL_REGEX, 'Must be 10-16 digits');

  const onCustomScriptBlur = (field) => {
    const value = form[field];
    setErrors((e) => ({
      ...e,
      [field]: value && INLINE_SCRIPT_REGEX.test(value)
        ? 'Note: inline scripts may be blocked by CSP'
        : null,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.updated_at;
      const res = await axios.put(`${API_URL}/admin/settings/analytics`, payload, { headers });
      setUpdatedAt(res.data?.config?.updated_at || new Date().toISOString());
      toast.success('Analytics settings saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save analytics settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="admin-analytics-settings">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">Analytics & Tracking</h1>
        <p className="text-muted-foreground">Add tracking scripts to user-facing pages without touching code</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5" /> Master Switch
              </CardTitle>
              <CardDescription>Disable all tracking with one toggle</CardDescription>
            </div>
            <Switch
              checked={form.analytics_enabled}
              onCheckedChange={(v) => update('analytics_enabled', v)}
              data-testid="analytics-master-switch"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <Separator />

          {/* GA4 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Google Analytics 4</Label>
              <Switch checked={form.ga4_enabled} onCheckedChange={(v) => update('ga4_enabled', v)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Measurement ID</Label>
              <Input
                value={form.ga4_measurement_id}
                onChange={(e) => update('ga4_measurement_id', e.target.value)}
                onBlur={onGa4Blur}
                placeholder="G-XXXXXXXXXX"
              />
              {errors.ga4_measurement_id ? (
                <p className="text-xs text-red-500 mt-1">{errors.ga4_measurement_id}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Find this in GA4 → Admin → Data Streams</p>
              )}
            </div>
          </div>

          <Separator />

          {/* GTM */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Google Tag Manager</Label>
              <Switch checked={form.gtm_enabled} onCheckedChange={(v) => update('gtm_enabled', v)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Container ID</Label>
              <Input
                value={form.gtm_container_id}
                onChange={(e) => update('gtm_container_id', e.target.value)}
                onBlur={onGtmBlur}
                placeholder="GTM-XXXXXXX"
              />
              {errors.gtm_container_id ? (
                <p className="text-xs text-red-500 mt-1">{errors.gtm_container_id}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Note: GTM can manage GA4 — use one or the other</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Meta Pixel */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Meta (Facebook) Pixel</Label>
              <Switch checked={form.meta_pixel_enabled} onCheckedChange={(v) => update('meta_pixel_enabled', v)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Pixel ID</Label>
              <Input
                value={form.meta_pixel_id}
                onChange={(e) => update('meta_pixel_id', e.target.value)}
                onBlur={onMetaPixelBlur}
                placeholder="1234567890"
              />
              {errors.meta_pixel_id ? (
                <p className="text-xs text-red-500 mt-1">{errors.meta_pixel_id}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Find this in Meta Events Manager</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Custom Scripts */}
          <div className="space-y-3">
            <Label>Custom Scripts</Label>
            <div>
              <Label className="text-xs text-muted-foreground">&lt;head&gt; section (before &lt;/head&gt;)</Label>
              <Textarea
                value={form.custom_head_scripts}
                onChange={(e) => update('custom_head_scripts', e.target.value)}
                onBlur={() => onCustomScriptBlur('custom_head_scripts')}
                placeholder="<script>...</script>"
                rows={4}
                className="font-mono text-xs"
              />
              {errors.custom_head_scripts && (
                <p className="text-xs text-amber-600 mt-1">{errors.custom_head_scripts}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">&lt;body&gt; section (after &lt;body&gt;)</Label>
              <Textarea
                value={form.custom_body_scripts}
                onChange={(e) => update('custom_body_scripts', e.target.value)}
                onBlur={() => onCustomScriptBlur('custom_body_scripts')}
                placeholder="<script>...</script>"
                rows={4}
                className="font-mono text-xs"
              />
              {errors.custom_body_scripts && (
                <p className="text-xs text-amber-600 mt-1">{errors.custom_body_scripts}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">Use for: Hotjar, Crisp chat, custom pixels</p>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Custom scripts run on all user-facing pages. Only paste scripts from trusted sources.</span>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-analytics-settings-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Analytics Settings
            </Button>
            {updatedAt && (
              <span className="text-xs text-muted-foreground">
                Last saved: {new Date(updatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalyticsSettings;
